import { useQuery } from "@tanstack/react-query";
import { blogPostsQuery, LOAD_ERROR_MESSAGE } from "@/lib/publicQueries";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { Link } from "@/lib/router-compat";
import { Calendar, User, ArrowRight, Tag } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import imgPhotoPrep from "@/assets/blog/photoshoot-preparation.jpg";
import imgVideoMarketing from "@/assets/blog/video-marketing.jpg";
import imgRealEstate from "@/assets/blog/real-estate-photography.jpg";
import imgImagefilmCosts from "@/assets/blog/imagefilm-costs.jpg";
import imgMannheimLocations from "@/assets/blog/mannheim-locations.jpg";

// Specific fallbacks for posts whose remote images are missing
const FALLBACK_BY_SLUG: Record<string, string> = {
  "instagram-reels-strategie-unternehmen": imgVideoMarketing,
  "professionelle-fotografie-mannheim-tipps": imgPhotoPrep,
};

const Blog = () => {
  const { data: posts, isLoading, isError } = useQuery(blogPostsQuery());

const IMAGE_MAP: Record<string, string> = {
  "/blog/photoshoot-preparation.jpg": imgPhotoPrep,
  "/blog/video-marketing.jpg": imgVideoMarketing,
  "/blog/real-estate-photography.jpg": imgRealEstate,
  "/blog/imagefilm-costs.jpg": imgImagefilmCosts,
  "/blog/mannheim-locations.jpg": imgMannheimLocations,
  "/src/assets/locations/mannheim-hero.jpg": imgMannheimLocations,
  "/src/assets/services/videography-professional.jpg": imgImagefilmCosts,
  // Broken remote URLs mapped to local fallbacks
  "https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/gallery/1760101610766-hep3jl2k8u.jpg": imgVideoMarketing,
  "https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/gallery/1760101649329-hep3jl5tpq.jpg": imgPhotoPrep,
};

  const mapImageUrl = (url?: string) => {
    if (!url) return undefined;
    return IMAGE_MAP[url] || url;
  };

  return (
    <>
      <BreadcrumbSchema items={[{ name: "Startseite", url: "https://clicksdmp.com/" }, { name: "Blog", url: "https://clicksdmp.com/blog" }]} />
      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">
                Blog
              </h1>
              <p className="text-4xl md:text-5xl lg:text-6xl font-bold">
                Blog & Insights
              </p>
              <p className="text-lg md:text-xl text-muted-foreground">
                Wissenswertes zu Videoproduktion, Fotografie und Digital Marketing
                aus der Praxis
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            {isError && !posts ? (
              <p role="alert" className="text-center text-muted-foreground py-12">{LOAD_ERROR_MESSAGE}</p>
            ) : isLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <div className="h-64 bg-muted rounded-t-lg" />
                    <CardContent className="p-6 space-y-4">
                      <div className="h-4 bg-muted rounded-sm w-3/4" />
                      <div className="h-4 bg-muted rounded-sm w-full" />
                      <div className="h-4 bg-muted rounded-sm w-5/6" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : posts && posts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group"
                  >
                    <Card className="h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                      {post.featured_image && (
                        <div className="relative h-64 overflow-hidden rounded-t-lg">
                          <img
                            src={mapImageUrl(post.featured_image)}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = FALLBACK_BY_SLUG[post.slug] || '/placeholder.svg'; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        </div>
                      )}
                      <CardHeader>
                        <div className="flex flex-wrap gap-2 mb-4">
                          <Badge variant="secondary">{post.category}</Badge>
                          {post.tags?.slice(0, 2).map((tag: string) => (
                            <Badge key={tag} variant="outline">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <h2 className="text-2xl font-bold group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-muted-foreground line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {format(
                                new Date(post.published_at || post.created_at),
                                "dd. MMMM yyyy",
                                { locale: de }
                              )}
                            </span>
                          </div>
                          <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-lg text-muted-foreground">
                  Noch keine Blogbeiträge verfügbar.
                </p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
